import type { ISlackMessageLogEntry } from '@skystone/shared';
import { Badge, Button } from '@/components/ui';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { formatDateTime } from '@/lib/date-utils';

interface SlackMessageLogCardProps {
  logEntries: ISlackMessageLogEntry[];
  logPage: number;
  totalLogPages: number;
  logTotal: number;
  onPageChange: (page: number) => void;
}

export function SlackMessageLogCard({
  logEntries,
  logPage,
  totalLogPages,
  logTotal,
  onPageChange,
}: Readonly<SlackMessageLogCardProps>) {
  if (logEntries.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Message Log</CardTitle>
        <CardDescription>
          Recent Slack message delivery attempts.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 pr-4 font-medium">Timestamp</th>
                <th className="pb-2 pr-4 font-medium">Recipient</th>
                <th className="pb-2 pr-4 font-medium">Type</th>
                <th className="pb-2 pr-4 font-medium">Status</th>
                <th className="pb-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody>
              {logEntries.map((entry) => (
                <tr key={entry.id} className="border-b last:border-0">
                  <td className="py-2 pr-4 text-muted-foreground">
                    {formatDateTime(entry.createdAt)}
                  </td>
                  <td className="py-2 pr-4 text-foreground">
                    {entry.recipientEmail ?? entry.recipientSlackId}
                  </td>
                  <td className="py-2 pr-4 text-muted-foreground">
                    {entry.messageType}
                  </td>
                  <td className="py-2 pr-4">
                    <Badge variant={entry.success ? 'success' : 'error'}>
                      {entry.success ? 'Sent' : 'Failed'}
                    </Badge>
                  </td>
                  <td className="py-2 text-xs text-muted-foreground">
                    {entry.errorMessage ?? '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalLogPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Page {logPage} of {totalLogPages} ({logTotal} total)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={logPage <= 1}
                onClick={() => onPageChange(logPage - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={logPage >= totalLogPages}
                onClick={() => onPageChange(logPage + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
